import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { PublicEventsService } from '../../services/public-events/public-events.service';
import { ToastService } from '../../shared/services';
import { PublicEvent } from '../../shared/interfaces';
import { STATUS_DEF_MAP } from '../../shared/api/eventStatus.token';

@Component({
  selector: 'app-public-event',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './public-event.html',
  styleUrl: './public-event.css',
})
export class PublicEventComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private eventsService = inject(PublicEventsService);
  private toastService = inject(ToastService);
  private statusDefs = inject(STATUS_DEF_MAP);

  event = signal<PublicEvent | null>(null);
  isLoading = signal(true);
  isProcessing = signal(false);
  errorMessage = signal<string | null>(null);

  statusBadge = computed(() => {
    const evt = this.event();
    if (!evt) return null;
    const def = this.statusDefs[evt.statusCode as keyof typeof this.statusDefs];
    return {
      label: def?.name ?? evt.statusName,
      color: def?.color ?? '#6B7280',
    };
  });

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const eventId = params.get('eventId');
      if (eventId) {
        this.loadEvent(eventId);
      }
    });
  }

  loadEvent(eventId: string): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.eventsService.getEvent(eventId).subscribe({
      next: (evt) => {
        this.event.set(evt);
        this.isLoading.set(false);
      },
      error: (err) => {
        const message =
          err?.error?.message ?? 'No pudimos cargar la información del evento.';
        this.errorMessage.set(message);
        this.isLoading.set(false);
      },
    });
  }

  confirm(): void {
    const evt = this.event();
    if (!evt || this.isProcessing()) return;
    this.isProcessing.set(true);
    this.eventsService.confirmEvent(evt.eventId).subscribe({
      next: (response) => {
        this.toastService.show(
          response?.message ?? 'Tu asistencia quedó confirmada.',
          'success'
        );
        this.event.update((current) =>
          current
            ? {
                ...current,
                statusCode: 'confirmed',
                statusName: 'Confirmado',
              }
            : current
        );
        this.isProcessing.set(false);
      },
      error: (err) => {
        const message =
          err?.error?.message ??
          'No pudimos confirmar tu asistencia. Intenta nuevamente.';
        this.toastService.show(message, 'warning');
        this.isProcessing.set(false);
      },
    });
  }

  cancel(): void {
    const evt = this.event();
    if (!evt || this.isProcessing()) return;
    this.isProcessing.set(true);
    this.eventsService.cancelEvent(evt.eventId).subscribe({
      next: (response) => {
        this.toastService.show(
          response?.message ?? 'Tu cita fue cancelada.',
          'success'
        );
        this.event.update((current) =>
          current
            ? {
                ...current,
                statusCode: 'cancelled',
                statusName: 'Cancelado',
              }
            : current
        );
        this.isProcessing.set(false);
      },
      error: (err) => {
        const message =
          err?.error?.message ??
          'No pudimos cancelar tu cita en este momento.';
        this.toastService.show(message, 'warning');
        this.isProcessing.set(false);
      },
    });
  }

  isConfirmed(): boolean {
    return this.event()?.statusCode === 'confirmed';
  }

  isCancelled(): boolean {
    return this.event()?.statusCode === 'cancelled';
  }
}
